/**
 * One person's skill sheet — the digital version of the paper form:
 * profile block, dated assessment columns (latest first), a new-assessment
 * form with per-skill level pickers, feedback block and the three-step
 * proposed → checked → approved sign-off.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Plus, Save, CheckCircle2, ShieldCheck, Trash2,
  TrendingUp, TrendingDown, IdCard, BadgeCheck, Pencil, ClipboardList, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls, todayISO } from '../../../components/admin/billing/shared';
import { LevelChip, LevelPicker, SkillLegend, indexTone } from './shared';
import { AssessmentEditLog } from './AssessmentEditLog';
import { AiTrainingPlanCard } from './AiTrainingPlanCard';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { invalidateExperts } from '../../../components/admin/SkillBadge';

const PROFILE_FIELDS = [
  { key: 'age', label: 'Age', type: 'number' },
  { key: 'phone', label: 'Phone' },
  { key: 'residence_address', label: 'Residence' },
  { key: 'languages_spoken', label: 'Languages (speak)', placeholder: 'e.g. Kannada, Hindi' },
  { key: 'languages_read_write', label: 'Languages (read & write)', placeholder: 'e.g. English' },
  { key: 'prior_experience', label: 'Prior experience', placeholder: 'e.g. 3 yrs at ABC Motors' },
];

const profileFrom = (st) => ({
  age: st.age ?? '',
  phone: st.phone ?? '',
  residence_address: st.residence_address ?? '',
  languages_spoken: st.languages_spoken ?? '',
  languages_read_write: st.languages_read_write ?? '',
  prior_experience: st.prior_experience ?? '',
  role_summary: st.role_summary ?? '',
  responsibilities_text: (st.responsibilities || []).join('\n'),
  id_proof_on_file: !!st.id_proof_on_file,
  id_proof_type: st.id_proof_type ?? '',
  reporting_manager_id: st.reporting_manager_id ?? '',
});

const SkillSheetPage = () => {
  const { sid } = useParams();
  const { authHeader, user } = useAuth();
  const canEditProfile = !!(user?.is_master_admin || (user?.permissions || {}).can_manage_staff);
  // Editing a recorded assessment is reserved for the master admin login.
  const isMasterAdmin = !!user?.is_master_admin;
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [profile, setProfile] = useState({});
  const [managers, setManagers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState(null); // { assessment_date, ratings, notes, training_needs, feedback }
  const [templates, setTemplates] = useState([]);
  const [canEditTemplates, setCanEditTemplates] = useState(false);

  // Role duty templates for the Work-Responsibilities dropdown.
  useEffect(() => {
    if (!canEditProfile) return;
    axios.get(`${API}/skills/duty-templates`, { headers: authHeader() })
      .then(({ data: d }) => { setTemplates(d.templates || []); setCanEditTemplates(!!d.can_edit); })
      .catch(() => setTemplates([]));
  }, [authHeader, canEditProfile]);

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setProfile((p) => {
      const have = (p.responsibilities_text || '').split('\n').map((d) => d.trim()).filter(Boolean);
      const lower = new Set(have.map((d) => d.toLowerCase()));
      const added = (t.duties || []).filter((d) => !lower.has(d.trim().toLowerCase()));
      toast.success(added.length
        ? `Added ${added.length} duties from “${t.role}”`
        : `“${t.role}” duties are already on this profile`);
      return {
        ...p,
        role_summary: (p.role_summary || '').trim() || t.role_summary || '',
        responsibilities_text: [...have, ...added].join('\n'),
      };
    });
  };

  const saveAsTemplate = async () => {
    const role = (data?.staff?.role || '').trim();
    if (!role) { toast.error('Set a designation on this staff member first'); return; }
    const duties = (profile.responsibilities_text || '').split('\n').map((d) => d.trim()).filter(Boolean);
    if (!duties.length) { toast.error('Add at least one duty first'); return; }
    setBusy('template');
    try {
      const { data: t } = await axios.post(`${API}/skills/duty-templates`, {
        role, role_summary: profile.role_summary || '', duties,
      }, { headers: authHeader() });
      setTemplates((prev) => [...prev.filter((x) => x.id !== t.id), t]);
      toast.success(`Saved as the standard duty list for “${role}”`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  // Staff list for the reporting-manager picker — same source as Staff Master.
  useEffect(() => {
    if (!canEditProfile) return;
    axios.get(`${API}/staff`, { headers: authHeader() })
      .then(({ data: d }) => setManagers(
        (Array.isArray(d) ? d : d.items || []).filter((m) => m.status !== 'inactive')))
      .catch(() => setManagers([]));
  }, [authHeader, canEditProfile]);

  const saveProfile = async () => {
    setBusy('profile');
    try {
      const mgr = managers.find((m) => m.id === profile.reporting_manager_id);
      const { responsibilities_text: dutyText, ...rest } = profile;
      await axios.put(`${API}/staff/${sid}`, {
        ...rest,
        responsibilities: (dutyText || '').split('\n').map((d) => d.trim()).filter(Boolean),
        age: profile.age === '' || profile.age === null ? null : Number(profile.age),
        reporting_manager_id: profile.reporting_manager_id || '',
        reporting_manager_name: mgr ? mgr.name : '',
      }, { headers: authHeader() });
      toast.success('Profile updated');
      setEditProfile(false);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/skills/staff/${sid}`, { headers: authHeader() });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, sid]);

  useEffect(() => { load(); }, [load]);

  const latest = data?.assessments?.[0];

  const startDraft = () => setDraft({
    id: null,
    assessment_date: todayISO(),
    ratings: { ...(latest?.ratings || {}) },
    notes: '',
    training_needs: '',
    feedback: '',
  });

  // Master admin only — reopen a recorded assessment (including approved ones)
  // in the same form to correct ratings or any of the written sections.
  const startEdit = (a) => {
    setDraft({
      id: a.id,
      assessment_date: a.assessment_date || todayISO(),
      ratings: { ...(a.ratings || {}) },
      notes: a.notes || '',
      training_needs: a.training_needs || '',
      feedback: (a.feedback_points || []).join('\n'),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveDraft = async () => {
    setBusy('save');
    try {
      const body = {
        assessment_date: draft.assessment_date,
        ratings: draft.ratings,
        notes: draft.notes,
        training_needs: draft.training_needs,
        feedback_points: (draft.feedback || '').split('\n').filter(Boolean),
      };
      if (draft.id) {
        await axios.patch(`${API}/skills/assessments/${draft.id}`, body, { headers: authHeader() });
        toast.success('Assessment updated');
      } else {
        await axios.post(`${API}/skills/assessments`, { staff_id: sid, ...body },
                         { headers: authHeader() });
        toast.success('Assessment recorded');
      }
      invalidateExperts();
      setDraft(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const sign = async (aid, stage) => {
    setBusy(`${aid}-${stage}`);
    try {
      await axios.post(`${API}/skills/assessments/${aid}/sign/${stage}`, {}, { headers: authHeader() });
      toast.success(stage === 'checked' ? 'Marked as checked' : 'Approved');
      invalidateExperts();
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const removeAssessment = async () => {
    const a = confirmDelete;
    if (!a) return;
    setBusy(a.id);
    try {
      await axios.delete(`${API}/skills/assessments/${a.id}`, { headers: authHeader() });
      toast.success(`Assessment dated ${a.assessment_date} deleted`);
      invalidateExperts();
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  // Undo a correction — puts the values back to how they stood before the
  // chosen revision. The rollback itself is logged, so nothing is lost.
  const restoreVersion = async () => {
    const c = confirmRestore;
    if (!c) return;
    setBusy(`restore-${c.assessment.id}`);
    try {
      await axios.post(`${API}/skills/assessments/${c.assessment.id}/restore`,
                       { revision_index: c.index }, { headers: authHeader() });
      toast.success('Rolled back to the earlier version');
      invalidateExperts();
      setConfirmRestore(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const downloadCertificate = async () => {
    setBusy('cert');
    try {
      const res = await axios.get(`${API}/skills/certificate/${sid}`, {
        headers: authHeader(), responseType: 'blob', timeout: 60000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        const a = document.createElement('a');
        a.href = url; a.download = 'Performance_Appraisal.pdf';
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const grouped = useMemo(() => {
    const out = {};
    (data?.catalogue || []).forEach((s) => {
      out[s.group] = out[s.group] || [];
      out[s.group].push(s);
    });
    return out;
  }, [data]);

  if (loading) return <div className="p-12 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  if (err || !data) {
    return (
      <div className="space-y-3">
        <Link to="/admin/skills" className="text-sm text-slate-600 flex items-center gap-1"><ArrowLeft size={15} /> Skill matrix</Link>
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err || 'Not found'}</div>
      </div>
    );
  }

  const s = data.staff || {};
  const snap = data.snapshot || {};
  const columns = (data.assessments || []).slice(0, 4);

  return (
    <div className="space-y-5 pb-24" data-testid="skill-sheet-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/skills" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1">
            <ArrowLeft size={14} /> Skill matrix
          </Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">{s.name}</h1>
          <p className="text-sm text-slate-500">{s.role || '—'} · joined {s.joining_date || '—'}</p>
        </div>
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
          <div className="text-xs">
            <div className="text-slate-500">Skill index</div>
            <div className={`text-2xl font-semibold ${indexTone(snap.skill_index)}`} data-testid="skill-index">
              {snap.skill_index === null || snap.skill_index === undefined ? '—' : `${snap.skill_index}%`}
            </div>
            <div className="text-slate-500">
              {snap.assessment_date ? `assessed ${snap.assessment_date}` : 'not assessed yet'}
              {snap.delta !== null && snap.delta !== undefined && (
                <span className={`ml-1 inline-flex items-center gap-0.5 ${snap.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {snap.delta >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}{Math.abs(snap.delta)}
                </span>
              )}
            </div>
          </div>
          <Link to="/admin/skills/training" className="ml-1 text-xs text-teal-700 hover:text-teal-900 font-medium" data-testid="sheet-training-link">
            Training plan →
          </Link>
          {!!snap.strengths?.length && (
            <button
              onClick={downloadCertificate} disabled={busy === 'cert'}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm hover:bg-amber-100 disabled:opacity-50"
              data-testid="certificate-btn"
            >
              {busy === 'cert' ? <Loader2 size={15} className="animate-spin" /> : <BadgeCheck size={15} />}
              Appraisal PDF
            </button>
          )}
          {data.can_edit && !draft && (
            <button
              onClick={startDraft}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700"
              data-testid="new-assessment-btn"
            >
              <Plus size={15} /> New assessment
            </button>
          )}
        </div>
      </div>

      {/* Profile block — editable in place for staff admins */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <IdCard size={15} className="text-teal-600" /> Profile
          </h2>
          {canEditProfile && !editProfile && (
            <button
              onClick={() => { setProfile(profileFrom(s)); setEditProfile(true); }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
              data-testid="edit-profile-btn"
            >
              <Pencil size={13} /> Edit profile
            </button>
          )}
        </div>

        {editProfile ? (
          <div className="space-y-3" data-testid="profile-form">
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2.5">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                <ClipboardList size={13} className="text-teal-600" /> Work responsibilities
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[240px] flex-1">
                  <label className="text-[11px] uppercase tracking-wider text-slate-500">
                    Start from a role template
                  </label>
                  <select
                    value=""
                    onChange={(e) => { applyTemplate(e.target.value); e.target.value = ''; }}
                    className="mt-0.5 w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                    data-testid="duty-template-select"
                  >
                    <option value="">— pick a role to add its standard duties —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.role} ({(t.duties || []).length})
                      </option>
                    ))}
                  </select>
                </div>
                {canEditTemplates && (
                  <button
                    type="button" onClick={saveAsTemplate} disabled={busy === 'template'}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-teal-300 bg-teal-50 px-2.5 py-1.5 text-xs text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                    title={`Save this list as the standard for ${s.role || 'this role'}`}
                    data-testid="save-duty-template-btn"
                  >
                    {busy === 'template' ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    Save as {s.role || 'role'} template
                  </button>
                )}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Primary role summary</label>
                <input
                  type="text"
                  value={profile.role_summary ?? ''}
                  onChange={(e) => setProfile((p) => ({ ...p, role_summary: e.target.value }))}
                  placeholder="e.g. Leads PPF installation for the sedan bay"
                  className="mt-0.5 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                  data-testid="profile-role_summary"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Duties — one per line</label>
                <textarea
                  rows={5}
                  value={profile.responsibilities_text ?? ''}
                  onChange={(e) => setProfile((p) => ({ ...p, responsibilities_text: e.target.value }))}
                  placeholder={'PPF installation on sedans\nTrain juniors on wrapping\nDaily bay cleanliness check'}
                  className="mt-0.5 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                  data-testid="profile-responsibilities"
                />
                <p className="text-[11px] text-slate-400 mt-1">Each line becomes a bullet here and on the Appraisal PDF. Max 30.</p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {PROFILE_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500">{f.label}</label>
                  <input
                    type={f.type || 'text'}
                    value={profile[f.key] ?? ''}
                    onChange={(e) => setProfile((p) => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.placeholder || ''}
                    className="mt-0.5 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                    data-testid={`profile-${f.key}`}
                  />
                </div>
              ))}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500">ID proof</label>
                <div className="mt-1.5 flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-sm text-slate-700">
                    <input
                      type="checkbox" checked={!!profile.id_proof_on_file}
                      onChange={(e) => setProfile((p) => ({ ...p, id_proof_on_file: e.target.checked }))}
                      data-testid="profile-id_proof_on_file"
                    /> On file
                  </label>
                  <input
                    type="text" value={profile.id_proof_type ?? ''}
                    onChange={(e) => setProfile((p) => ({ ...p, id_proof_type: e.target.value }))}
                    placeholder="Aadhaar / PAN / DL"
                    className="flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                    data-testid="profile-id_proof_type"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Reporting manager</label>
                <select
                  value={profile.reporting_manager_id ?? ''}
                  onChange={(e) => setProfile((p) => ({ ...p, reporting_manager_id: e.target.value }))}
                  className="mt-0.5 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
                  data-testid="profile-reporting_manager_id"
                >
                  <option value="">— none —</option>
                  {managers.filter((m) => m.id !== s.id).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}{m.role ? ` · ${m.role}` : ''}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={saveProfile} disabled={busy === 'profile'}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:bg-slate-300"
                data-testid="save-profile-btn"
              >
                {busy === 'profile' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save profile
              </button>
              <button
                onClick={() => setEditProfile(false)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
                data-testid="cancel-profile-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4 rounded-lg border border-teal-100 bg-teal-50/50 p-3" data-testid="responsibilities-block">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-teal-800">
                <ClipboardList size={13} /> Work responsibilities
              </div>
              {s.role_summary && (
                <p className="mt-1.5 text-sm font-medium text-slate-900" data-testid="role-summary">{s.role_summary}</p>
              )}
              {(s.responsibilities || []).length > 0 ? (
                <ul className="mt-1.5 space-y-1">
                  {s.responsibilities.map((d, i) => (
                    <li key={`${i}-${d}`} className="flex gap-2 text-sm text-slate-700" data-testid={`responsibility-${i}`}>
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                !s.role_summary && (
                  <p className="mt-1.5 text-sm text-slate-500" data-testid="responsibilities-empty">
                    Not recorded yet{canEditProfile ? ' — add them via Edit profile.' : '.'}
                  </p>
                )
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-3 text-sm" data-testid="profile-block">
              {[
                ['Age', s.age || '—'],
                ['Phone', s.phone || '—'],
                ['ID proof', s.id_proof_on_file ? `On file${s.id_proof_type ? ` · ${s.id_proof_type}` : ''}` : 'Not submitted'],
                ['Residence', s.residence_address || '—'],
                ['Languages (speak)', s.languages_spoken || '—'],
                ['Languages (read & write)', s.languages_read_write || '—'],
                ['Prior experience', s.prior_experience || '—'],
                ['Reporting manager', s.reporting_manager_name || '—'],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div>
                  <div className="text-slate-900">{v}</div>
                </div>
              ))}
            </div>
            {!canEditProfile && (
              <p className="text-[11px] text-slate-400 mt-3">Edit these fields in Staff Master.</p>
            )}
          </>
        )}
      </div>

      <SkillLegend levels={data.levels || []} />

      {/* New assessment form */}
      {draft && (
        <div className="bg-white border border-teal-200 rounded-xl p-4 space-y-4" data-testid="assessment-form">
          {draft.id && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertCircle size={14} />
              <span>
                Editing the assessment recorded on <b>{draft.assessment_date}</b>. Changes are
                saved against the same record and stamped with your name. You can leave skills
                unrated and save a part-finished assessment.
              </span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Assessment date</label>
              <input
                type="date" className={inputCls} value={draft.assessment_date}
                onChange={(e) => setDraft({ ...draft, assessment_date: e.target.value })}
                data-testid="assessment-date"
              />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={saveDraft} disabled={busy === 'save'}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
                data-testid="save-assessment-btn"
              >
                {busy === 'save' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {draft.id ? 'Save changes' : 'Save assessment'}
              </button>
              <button onClick={() => setDraft(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600">Cancel</button>
            </div>
          </div>

          {Object.entries(grouped).map(([group, skills]) => (
            <div key={group}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{group}</div>
              <div className="space-y-1.5">
                {skills.map((sk) => (
                  <div key={sk.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-1.5">
                    <span className="text-sm text-slate-800">{sk.name}</span>
                    <div className="flex items-center gap-3">
                      {latest?.ratings?.[sk.id] && (
                        <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                          prev <LevelChip level={latest.ratings[sk.id]} size="sm" />
                        </span>
                      )}
                      <LevelPicker
                        levels={data.levels}
                        value={draft.ratings[sk.id]}
                        onChange={(k) => setDraft({ ...draft, ratings: { ...draft.ratings, [sk.id]: k } })}
                        testid={`rate-${sk.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Training needs</label>
              <textarea
                rows={3} className={inputCls} value={draft.training_needs}
                onChange={(e) => setDraft({ ...draft, training_needs: e.target.value })}
                data-testid="training-needs-input"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Feedback / asks from the team member (one per line)
              </label>
              <textarea
                rows={3} className={inputCls} value={draft.feedback}
                onChange={(e) => setDraft({ ...draft, feedback: e.target.value })}
                data-testid="feedback-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-slate-500 mb-1">Notes from the face-to-face</label>
              <textarea
                rows={2} className={inputCls} value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                data-testid="assessment-notes-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* Proposed training plan, read off the latest assessment */}
      <AiTrainingPlanCard staffId={sid} staffName={s.name} canEdit={!!data.can_edit} />

      {/* History grid — skills as rows, assessments as columns */}
      {!columns.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500" data-testid="no-assessments">
          No assessments recorded yet.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]" data-testid="history-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500">Skill</th>
                {columns.map((a) => (
                  <th key={a.id} className="px-3 py-2 text-[11px] text-slate-600 text-center whitespace-nowrap">
                    {a.assessment_date}
                    <div className="font-normal text-slate-400">{a.status}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Object.entries(grouped).map(([group, skills]) => (
                <React.Fragment key={group}>
                  <tr className="bg-slate-50/60">
                    <td colSpan={columns.length + 1} className="px-4 py-1 text-[11px] uppercase tracking-wider text-slate-500">{group}</td>
                  </tr>
                  {skills.map((sk) => (
                    <tr key={sk.id} data-testid={`hist-row-${sk.id}`}>
                      <td className="px-4 py-1.5 text-slate-800">{sk.name}</td>
                      {columns.map((a) => (
                        <td key={a.id} className="px-3 py-1.5 text-center">
                          <LevelChip level={a.ratings?.[sk.id]} size="sm" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr className="bg-slate-50/60 font-semibold">
                <td className="px-4 py-2 text-slate-700">Skill index</td>
                {columns.map((a) => (
                  <td key={a.id} className={`px-3 py-2 text-center font-mono ${indexTone(a.skill_index)}`}>
                    {a.skill_index === null ? '—' : `${a.skill_index}%`}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Sign-off + notes per assessment */}
      {columns.map((a) => (
        <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`assessment-${a.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-900">
              Assessment · {a.assessment_date}
              {a.edited_by && (
                <span
                  className="ml-2 align-middle inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800"
                  title={`Edited by ${a.edited_by} on ${(a.edited_at || '').slice(0, 16).replace('T', ' ')}`}
                  data-testid={`edited-badge-${a.id}`}
                >
                  <Pencil size={9} /> Edited by {a.edited_by} · {(a.edited_at || '').slice(0, 10)}
                  {a.edit_count > 1 ? ` (${a.edit_count}×)` : ''}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isMasterAdmin && (
                <button
                  onClick={() => startEdit(a)} disabled={!!draft}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                  data-testid={`edit-assessment-${a.id}`}
                  title="Correct this assessment"
                >
                  <Pencil size={12} /> Edit
                </button>
              )}
              {data.can_edit && !a.checked_by && (
                <button
                  onClick={() => sign(a.id, 'checked')} disabled={busy === `${a.id}-checked`}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`check-btn-${a.id}`}
                >
                  <CheckCircle2 size={12} /> Mark checked
                </button>
              )}
              {data.is_hr && a.checked_by && !a.approved_by && (
                <button
                  onClick={() => sign(a.id, 'approved')} disabled={busy === `${a.id}-approved`}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid={`approve-btn-${a.id}`}
                >
                  <ShieldCheck size={12} /> Approve
                </button>
              )}
              {data.is_hr && (
                <button
                  onClick={() => setConfirmDelete(a)} disabled={busy === a.id}
                  className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                  data-testid={`delete-assessment-${a.id}`}
                  title="Delete this assessment"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-3 mt-3 text-xs">
            {[['Proposed by', a.proposed_by], ['Checked by', a.checked_by], ['Approved by', a.approved_by]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div>
                <div className="text-slate-900 truncate">{v || 'pending'}</div>
              </div>
            ))}
          </div>
          {a.training_needs && (
            <p className="text-sm text-slate-700 mt-3"><span className="text-slate-500">Training needs: </span>{a.training_needs}</p>
          )}
          {!!(a.feedback_points || []).length && (
            <div className="mt-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Feedback / asks</div>
              <ul className="list-disc pl-5 text-sm text-slate-700">
                {a.feedback_points.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
          {a.notes && <p className="text-sm text-slate-700 mt-2"><span className="text-slate-500">Notes: </span>{a.notes}</p>}
          <AssessmentEditLog
            log={a.edit_log}
            catalogue={data.catalogue || []}
            onRestore={isMasterAdmin
              ? (index, rev) => setConfirmRestore({ assessment: a, index, rev })
              : undefined}
          />
        </div>
      ))}

      {/* Delete needs a deliberate confirmation — it takes the ratings,
          sign-offs and edit history with it. */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-assessment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this assessment?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  You're about to remove the assessment for <b>{s.name}</b> dated{' '}
                  <b>{confirmDelete?.assessment_date}</b>
                  {confirmDelete?.status ? <> (status: <b>{confirmDelete.status}</b>)</> : null}.
                </p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                  <div>Skill index: <b>{confirmDelete?.skill_index === null ? '—' : `${confirmDelete?.skill_index}%`}</b></div>
                  <div>Skills rated: <b>{Object.keys(confirmDelete?.ratings || {}).length}</b></div>
                  <div>Proposed by: <b>{confirmDelete?.proposed_by || '—'}</b></div>
                  {confirmDelete?.approved_by && <div>Approved by: <b>{confirmDelete.approved_by}</b></div>}
                </div>
                <p className="text-red-600">
                  This cannot be undone — the ratings, sign-offs and edit history go with it.
                  If you only need to fix a value, use <b>Edit</b> instead.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-assessment-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={removeAssessment}
              disabled={busy === confirmDelete?.id}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-assessment-confirm"
            >
              {busy === confirmDelete?.id ? 'Deleting…' : 'Delete assessment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Undo a correction */}
      <AlertDialog open={!!confirmRestore} onOpenChange={(o) => !o && setConfirmRestore(null)}>
        <AlertDialogContent data-testid="restore-revision-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this correction?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  The assessment dated <b>{confirmRestore?.assessment?.assessment_date}</b> will
                  go back to how it stood before the change made on{' '}
                  <b>{(confirmRestore?.rev?.at || '').slice(0, 16).replace('T', ' ')}</b>
                  {confirmRestore?.rev?.by ? <> by <b>{confirmRestore.rev.by}</b></> : null}.
                </p>
                <p className="text-xs">
                  These values will be put back:
                </p>
                <ul className="list-disc pl-5 text-xs">
                  {(confirmRestore?.rev?.changes || []).map((c, i) => (
                    <li key={i}>
                      {c.field === 'rating'
                        ? `${(data.catalogue || []).find((s) => s.id === c.skill_id)?.name || 'Skill'}: back to ${(c.from || 'not assessed').replace(/_/g, ' ')}`
                        : c.field === 'skill_index'
                          ? `Skill index: back to ${c.from === null || c.from === undefined ? '—' : `${c.from}%`}`
                          : `${c.label}: back to ${c.from === '' || c.from === null ? 'empty' : `“${c.from}”`}`}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500">
                  Any later corrections are rolled back too, and the undo is itself
                  recorded in the edit history.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-revision-cancel">Leave it</AlertDialogCancel>
            <AlertDialogAction
              onClick={restoreVersion}
              disabled={busy === `restore-${confirmRestore?.assessment?.id}`}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="restore-revision-confirm"
            >
              {busy === `restore-${confirmRestore?.assessment?.id}` ? 'Rolling back…' : 'Undo correction'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SkillSheetPage;
